import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { MyProfile } from '@onestep/camel-api';

import { getPortalRuntimeConfig } from '../services/runtimeConfig';
import { getDisplayName } from '../utils/nameUtil';
import { isAutomationEnvironment } from './automation';
import { hasAbortedRequestContext } from './datadogBeforeSend';
import getEnvironment from './environment';

let datadogEnabled = false;

export const TARGET_UUID = getPortalRuntimeConfig().datadog.targetRumUuid || '';
export const initDataDog = () => {
  const { rumAppId, clientToken } = getPortalRuntimeConfig().datadog;
  if (!rumAppId || !clientToken) {
    if (!getEnvironment().isProd) console.debug('No data dog keys found');
    datadogEnabled = false;
    return;
  }

  if (isAutomationEnvironment()) {
    if (!getEnvironment().isProd) console.warn('Datadog disabled in automation environment');
    datadogEnabled = false;
    return;
  }

  datadogEnabled = true;
  if (!getEnvironment().isProd) console.debug('Init data dog');
  initDataDogLogs();
};

const { envName } = getEnvironment();

const initDataDogLogs = () => {
  if (!getEnvironment().isProd) console.debug('Init data dog logs');

  datadogLogs.init({
    clientToken: getPortalRuntimeConfig().datadog.clientToken as string,
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    trackSessionAcrossSubdomains: true,
    sessionSampleRate: 100,
    env: envName,
    service: 'onestep-web',
    forwardConsoleLogs: ['error', 'debug'],
    beforeSend: (_log, context) => !hasAbortedRequestContext(context),
  });
};

export const setUser = (myProfile: MyProfile) => {
  if (!datadogEnabled) {
    return;
  }

  datadogLogs.setUser({
    id: myProfile.uuid,
    name: getDisplayName(myProfile),
    email: myProfile.email,
    username: myProfile.username,
  });
};

export const setRumUser = (myProfile: MyProfile, sampleRate: number = 10) => {
  if (!datadogEnabled) {
    return;
  }
  if (!getEnvironment().isProd) console.debug('Initializing RUM for target user');
  const { rumAppId, clientToken } = getPortalRuntimeConfig().datadog;
  datadogRum.init({
    applicationId: rumAppId as string,
    clientToken: clientToken as string,
    site: 'datadoghq.com',
    service: 'onestep-web',
    env: envName,
    sessionSampleRate: sampleRate,
    sessionReplaySampleRate: sampleRate,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });

  datadogRum.setUser({
    id: myProfile.uuid,
    name: getDisplayName(myProfile),
    email: myProfile.email,
    username: myProfile.username,
  });
};

export const clearUser = () => {
  if (!datadogEnabled) {
    return;
  }
  datadogLogs.setUser({
    id: 'logged-out',
    name: 'logged-out',
    email: 'logged-out',
    username: 'logged-out',
  });

  datadogRum.clearUser();
};
